import {
    Descendant,
    Editor,
    Range,
    Text,
} from 'slate';

import { HEADER_SIZE } from '../components/Editor/elements/enums';
import { LIST_TYPE } from '../components/Editor/elements/listElement/enums';
import { EMPHASIZER_MARK } from '../components/Editor/plugins/enums';

const BLOCK_QUOTE_TYPE = 'block-quote';
const HEADER_TYPE = 'header';
const LINE_TYPE = 'line';
const LIST_TYPE_NAME = 'list';
const LIST_ITEM_TYPE = 'list-item';
const WEB_LINK_TYPE = 'web-link';
const ZERO_WIDTH_NO_BREAK_SPACE = /\uFEFF/g;

type ElementNode = {
    type: string,
    children: Descendant[],
    href?: string,
    headerSize?: HEADER_SIZE,
    listType?: LIST_TYPE,
};

interface ISerializeSlateSelectionForClipboardArgs {
    editor: Editor,
    selection: Range,
}

interface ISerializeSlateSelectionForClipboardResult {
    text: string,
    html: string,
}

const isElementNode = (node: Descendant): node is ElementNode => !Text.isText(node)
    && 'type' in node
    && 'children' in node
    && Array.isArray(node.children);

const escapeHtml = (value: string): string => value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeText = (value: string): string => value.replace(ZERO_WIDTH_NO_BREAK_SPACE, '');

const wrapPlainTextMarks = (value: string, leaf: Text): string => {
    if (value.length === 0) return value;

    let wrapped = value;
    if (leaf[EMPHASIZER_MARK.bold]) wrapped = `**${wrapped}**`;
    if (leaf[EMPHASIZER_MARK.italicize]) wrapped = `*${wrapped}*`;
    if (leaf[EMPHASIZER_MARK.strikethrough]) wrapped = `~~${wrapped}~~`;
    if (leaf[EMPHASIZER_MARK.underline]) wrapped = `<u>${wrapped}</u>`;
    return wrapped;
};

const wrapHtmlMarks = (value: string, leaf: Text): string => {
    if (value.length === 0) return value;

    let wrapped = value;
    if (leaf[EMPHASIZER_MARK.bold]) wrapped = `<strong>${wrapped}</strong>`;
    if (leaf[EMPHASIZER_MARK.italicize]) wrapped = `<em>${wrapped}</em>`;
    if (leaf[EMPHASIZER_MARK.strikethrough]) wrapped = `<s>${wrapped}</s>`;
    if (leaf[EMPHASIZER_MARK.underline]) wrapped = `<u>${wrapped}</u>`;
    return wrapped;
};

const serializeInlineNodeToPlainText = (node: Descendant): string => {
    if (Text.isText(node)) {
        return wrapPlainTextMarks(sanitizeText(node.text), node);
    }

    if (!isElementNode(node)) return '';

    if (node.type === WEB_LINK_TYPE) {
        const label = serializeInlineNodesToPlainText(node.children);
        const href = node.href || '';
        if (!href) return label;
        const resolvedLabel = label.length > 0 ? label : href;
        return `[${resolvedLabel}](${href})`;
    }

    return serializeInlineNodesToPlainText(node.children);
};

const serializeInlineNodesToPlainText = (nodes: Descendant[]): string => nodes
    .map((node) => serializeInlineNodeToPlainText(node))
    .join('');

const serializeInlineNodeToHtml = (node: Descendant): string => {
    if (Text.isText(node)) {
        return wrapHtmlMarks(escapeHtml(sanitizeText(node.text)), node);
    }

    if (!isElementNode(node)) return '';

    if (node.type === WEB_LINK_TYPE) {
        const label = serializeInlineNodesToHtml(node.children);
        const href = escapeHtml(node.href || '');
        if (!href) return label;
        const resolvedLabel = label.length > 0 ? label : href;
        return `<a href="${href}">${resolvedLabel}</a>`;
    }

    return serializeInlineNodesToHtml(node.children);
};

const serializeInlineNodesToHtml = (nodes: Descendant[]): string => nodes
    .map((node) => serializeInlineNodeToHtml(node))
    .join('');

const prefixBlockQuoteLines = (value: string): string => value
    .split('\n')
    .map((line) => (line.length > 0 ? `> ${line}` : '>'))
    .join('\n');

const serializeListItemToPlainText = (
    node: ElementNode,
    depth: number,
    listType: LIST_TYPE,
    index: number,
): string => {
    const nonListChildren = node.children.filter((child) => !isElementNode(child) || child.type !== LIST_TYPE_NAME);
    const nestedLists = node.children.filter((child): child is ElementNode => isElementNode(child) && child.type === LIST_TYPE_NAME);
    const marker = listType === LIST_TYPE.ordered ? `${index}. ` : '- ';
    const indent = '  '.repeat(depth);
    const continuationPrefix = `${indent}${' '.repeat(marker.length)}`;
    const content = serializeNodesToPlainText(nonListChildren);
    const lines: string[] = [];

    if (content.length > 0) {
        const contentLines = content.split('\n');
        lines.push(`${indent}${marker}${contentLines[0]}`);
        contentLines.slice(1).forEach((line) => {
            lines.push(`${continuationPrefix}${line}`);
        });
    } else {
        lines.push(`${indent}${marker.trimEnd()}`);
    }

    nestedLists.forEach((child) => {
        const nestedText = serializeNodeToPlainText(child, depth + 1);
        if (nestedText.length > 0) {
            lines.push(nestedText);
        }
    });

    return lines.join('\n');
};

const serializeNodeToPlainText = (node: Descendant, listDepth = 0): string => {
    if (Text.isText(node)) return wrapPlainTextMarks(sanitizeText(node.text), node);
    if (!isElementNode(node)) return '';

    if (node.type === LIST_TYPE_NAME) {
        return node.children
            .filter((child): child is ElementNode => isElementNode(child) && child.type === LIST_ITEM_TYPE)
            .map((child, index) => serializeListItemToPlainText(
                child,
                listDepth,
                node.listType || LIST_TYPE.unordered,
                index + 1,
            ))
            .join('\n');
    }

    if (node.type === LIST_ITEM_TYPE) {
        return serializeListItemToPlainText(node, listDepth, LIST_TYPE.unordered, 1);
    }

    if (node.type === HEADER_TYPE) {
        const headerPrefix = node.headerSize === HEADER_SIZE.two ? '## ' : '# ';
        return `${headerPrefix}${serializeInlineNodesToPlainText(node.children)}`;
    }

    if (node.type === BLOCK_QUOTE_TYPE) {
        return prefixBlockQuoteLines(serializeNodesToPlainText(node.children));
    }

    if (node.type === LINE_TYPE) {
        return serializeInlineNodesToPlainText(node.children);
    }

    if (node.type === WEB_LINK_TYPE) {
        return serializeInlineNodeToPlainText(node);
    }

    return serializeNodesToPlainText(node.children);
};

const serializeNodeToHtml = (node: Descendant): string => {
    if (Text.isText(node)) return wrapHtmlMarks(escapeHtml(sanitizeText(node.text)), node);
    if (!isElementNode(node)) return '';

    if (node.type === LIST_TYPE_NAME) {
        const tag = node.listType === LIST_TYPE.ordered ? 'ol' : 'ul';
        return `<${tag}>${node.children.map((child) => serializeNodeToHtml(child)).join('')}</${tag}>`;
    }

    if (node.type === LIST_ITEM_TYPE) {
        return `<li>${node.children.map((child) => serializeNodeToHtml(child)).join('')}</li>`;
    }

    if (node.type === HEADER_TYPE) {
        const tag = node.headerSize === HEADER_SIZE.two ? 'h2' : 'h1';
        return `<${tag}>${serializeInlineNodesToHtml(node.children)}</${tag}>`;
    }

    if (node.type === BLOCK_QUOTE_TYPE) {
        return `<blockquote>${node.children.map((child) => serializeNodeToHtml(child)).join('')}</blockquote>`;
    }

    if (node.type === LINE_TYPE) {
        return `<p>${serializeInlineNodesToHtml(node.children)}</p>`;
    }

    if (node.type === WEB_LINK_TYPE) {
        return serializeInlineNodeToHtml(node);
    }

    return node.children.map((child) => serializeNodeToHtml(child)).join('');
};

const serializeNodesToPlainText = (nodes: Descendant[]): string => nodes
    .map((node) => serializeNodeToPlainText(node))
    .filter((part) => part.length > 0)
    .join('\n\n');

const serializeNodesToHtml = (nodes: Descendant[]): string => nodes
    .map((node) => serializeNodeToHtml(node))
    .join('');

function serializeSlateSelectionForClipboard({
    editor,
    selection,
}: ISerializeSlateSelectionForClipboardArgs): ISerializeSlateSelectionForClipboardResult | null {
    if (!Range.isRange(selection)) return null;

    try {
        const fragment = Editor.fragment(editor, selection);
        const text = serializeNodesToPlainText(fragment);
        const html = serializeNodesToHtml(fragment);

        if (text.length === 0 && html.length === 0) {
            return null;
        }

        return {
            text,
            html,
        };
    } catch (err) {
        return null;
    }
}

export default serializeSlateSelectionForClipboard;
